class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    window.casSearch?.addEventListener('update', (event) => this.onCasUpdate(event.detail.keys));
  }

  // A background refresh of the CAS index changed these CAS numbers' products (assets/cas-search.js):
  // drop their cached results, and redraw the dropdown if it shows one of them.
  onCasUpdate(keys) {
    Object.entries(this.cachedResults).forEach(([queryKey, markup]) => {
      const casKey = markup.match(/data-cas-key="(\d+)"/)?.[1];
      if (casKey && keys.has(casKey)) delete this.cachedResults[queryKey];
    });
    if (!keys.has(window.casSearch.queryKey(this.searchTerm))) return;
    if (this.isOpen) {
      this.getSearchResults(this.searchTerm);
    } else {
      // Reopening the dropdown then loads the new results instead of showing the old ones.
      this.removeAttribute('results');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Shopify search doesn't cover the CAS number metafield; look a CAS number up alongside (assets/cas-search.js).
    const casResults = window.casSearch?.suggest(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then(async (text) => {
        let resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        const casResult = await casResults;
        // The CAS index was refreshed while this loaded: search again with the new one.
        if (window.casSearch?.isOutdated(casResult)) {
          if (searchTerm === this.searchTerm) this.getSearchResults(searchTerm);
          return;
        }
        if (casResult) resultsMarkup = this.addCasResults(resultsMarkup, casResult);
        // Save bandwidth keeping the cache in all instances synced. A failed CAS lookup isn't cached, so it's retried.
        if (casResult?.status !== 'error') {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
        }
        // A CAS lookup can take longer; don't let it overwrite the results of a newer term.
        if (searchTerm !== this.searchTerm) return;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // Adds a casSearch.suggest() result to the results markup: the products with that CAS number above the
  // other products, or a note when no product has it or the lookup failed.
  addCasResults(resultsMarkup, cas) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;
    const results = template.content.querySelector('#predictive-search-results');
    if (!results) return resultsMarkup;

    const strings = window.casSearch.strings;
    const liveRegion = template.content.querySelector('[data-predictive-search-live-region-count-value]');
    const productsList = results.querySelector('#predictive-search-results-products-list');
    // For onCasUpdate and the CAS search analytics.
    Object.assign(results.dataset, { casKey: cas.key, casQuery: cas.query, casStatus: cas.status, casCount: cas.count });

    if (cas.status === 'match') {
      const casText = `${strings.heading.replace('[query]', cas.query)} · ${window.casSearch.countText(cas.count)}`;
      const productsGroup = productsList?.closest('.predictive-search__result-group');
      const casPaths = new Set(cas.items.map((item) => this.getOptionPath(item)));
      productsList?.querySelectorAll('li').forEach((item) => {
        if (casPaths.has(this.getOptionPath(item))) item.remove();
      });
      if (productsList && !productsList.children.length) productsList.parentElement.remove();

      const casBlock = document.createElement('div');
      const heading = document.createElement('h2');
      heading.id = 'predictive-search-cas';
      heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
      heading.textContent = casText;
      const list = document.createElement('ul');
      list.id = 'predictive-search-results-cas-list';
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);
      list.append(...cas.items.map((item) => item.cloneNode(true)));
      casBlock.append(heading, list);

      let wrapper = results.querySelector('#predictive-search-results-groups-wrapper');
      const hadResults = Boolean(wrapper);
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.id = 'predictive-search-results-groups-wrapper';
        wrapper.className =
          'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
        results.prepend(wrapper);
      }
      wrapper.classList.remove('predictive-search__results-groups-wrapper--no-products');
      // The products column: the native one, else the second column (pages on mobile), else a new one.
      const groups = wrapper.querySelectorAll(':scope > .predictive-search__result-group');
      let group = productsGroup || (groups.length > 1 ? groups[groups.length - 1] : null);
      if (!group) {
        group = document.createElement('div');
        group.className = 'predictive-search__result-group';
        wrapper.append(group);
      }
      group.prepend(casBlock);

      if (liveRegion) {
        liveRegion.textContent = hadResults ? `${casText}. ${liveRegion.textContent.trim()}` : casText;
      }
    } else if (cas.status === 'error' || !productsList) {
      const notice = document.createElement('p');
      notice.className = 'predictive-search__cas-notice';
      notice.textContent =
        cas.status === 'error' ? strings.error : strings.noResults.replace('[query]', cas.query);
      results.prepend(notice);
      if (liveRegion) liveRegion.textContent = `${notice.textContent} ${liveRegion.textContent.trim()}`;
    }

    return template.innerHTML;
  }

  getOptionPath(option) {
    return new URL(option.querySelector('a')?.getAttribute('href') || '', window.location.origin).pathname;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.trackCasSearch();
  }

  // Records the CAS search on screen, once it has stayed there (assets/cas-search.js).
  trackCasSearch() {
    const results = this.querySelector('#predictive-search-results[data-cas-key]');
    if (!results || !window.casSearch) return;
    const { casKey: key, casQuery: query, casStatus: status, casCount: count } = results.dataset;
    window.casSearch.trackSearch(
      'predictive_search',
      { key, query, status, count: Number(count) },
      { settle: true, isCurrent: () => this.isOpen && window.casSearch.queryKey(this.getQuery()) === key }
    );
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
